// hooks, auth, navigation
import { useState, useEffect } from 'react';

// Material UI
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Select from '@mui/material/Select';
import Dialog from '@mui/material/Dialog';
import MDButton from "components/MDButton";
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';

// Material UI icons
import CloseIcon from '@mui/icons-material/Close';

const blockInvalidNumberKeys = (event) => {
    if (['e', 'E', '+', '-'].includes(event.key)) {
        event.preventDefault();
    }
};

const sanitizeNumberInput = (value) =>
    String(value ?? '').replace(/[eE+\-]/g, '');

const disabledOutlinedSx = {
    '& .MuiOutlinedInput-root.Mui-disabled': {
        borderRadius: '12px',
        backgroundColor: '#f5f5f5',
    },
    '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
        borderColor: '#e0e0e0',
    },
    '& .MuiInputBase-input.Mui-disabled': {
        WebkitTextFillColor: '#9e9e9e',
    },
};

const fieldErrorSx = {
    display: 'block',
    mt: 0.5,
    lineHeight: 1.4,
};


const PaymentTemplateModal = ({ open, onClose, onSave, template }) => {

    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        template_name: '',
        payment_type: '',
        status: 'draft',
        deposit_percentage: '',
        due_days_before_departure: '',
        number_of_installments: '',
        buffer_days: 0,
        installments: [],
    });

    // Validate input values.

    const validate = () => {
        const newErrors = {};

        // Template Name and Payment type must be filled.
        if (!formData.template_name) newErrors.template_name = '*This field is required';
        if (!formData.payment_type) newErrors.payment_type = '*This field is required';

        // Calculate deposit: required, and must be strictly between 0 and 100 (exclusive).
        const depositRaw = formData.deposit_percentage;
        const deposit = parseFloat(depositRaw);
        const isDepositEmpty = depositRaw === '' || depositRaw === null || depositRaw === undefined;
        const isDepositNotNumber = !isDepositEmpty && isNaN(deposit);
        const isDepositOutOfRange = !isDepositEmpty && !isNaN(deposit) && (deposit <= 0 || deposit >= 100);

        const getDepositError = () => {
            if (isDepositEmpty || isDepositNotNumber) return '*This field is required';
            if (isDepositOutOfRange) return 'Deposit % must be greater than 0 and less than 100';
            return null;
        };

        // IF payment type is selected as - deposit_final_balance : ensure additional fields are filled
        if (formData.payment_type === 'deposit_final_balance') {
            const depositError = getDepositError();
            if (depositError) newErrors.deposit_percentage = depositError;
            if (!formData.due_days_before_departure) newErrors.due_days_before_departure = '*This field is required';
        }

        // IF payment type is selected as - deposit_installments : ensure additional fields are filled
        if (formData.payment_type === 'deposit_installments') {
            const depositError = getDepositError();
            if (depositError) newErrors.deposit_percentage = depositError;
            if (!formData.number_of_installments) newErrors.number_of_installments = '*This field is required';

            // Each additional installment row must be filled.
            const installmentErrors = formData.installments.map((inst) => ({
                installment_percentage: !inst.installment_percentage ? '*This field is required' : false,
                days_before_departure: !inst.days_before_departure ? '*This field is required' : false,
            }));
            const hasInstallmentErrors = installmentErrors.some(e => e.installment_percentage || e.days_before_departure);

            if (hasInstallmentErrors) {
                newErrors.installments = installmentErrors;
            }

            // Check sum equals 100, only if deposit and all installment percentages are individually valid
            const isDepositInvalid = Boolean(depositError);
            if (!isDepositInvalid && !hasInstallmentErrors) {
                const installmentSum = formData.installments.reduce(
                    (sum, inst) => sum + (parseFloat(inst.installment_percentage) || 0), 0
                );
                const total = deposit + installmentSum;

                if (Math.round(total * 100) / 100 !== 100) {
                    newErrors.percentageSum = true;
                }
            }
        }

        if (['deposit_final_balance', 'deposit_installments'].includes(formData.payment_type)) {
            const raw = formData.buffer_days;
            if (raw !== '' && raw !== null && raw !== undefined) {
                const n = Number(raw);
                if (!Number.isInteger(n) || n < 0) {
                    newErrors.buffer_days = 'Buffer days must be a whole number (0 or more)';
                }
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    useEffect(() => {
        if (open) {
            if (template) {
                // Prefill the data if template prop is passed - when edit is performed.
                setFormData({
                    template_name: template.template_name || '',
                    status: template.status || 'draft',
                    payment_type: template.payment_type || '',
                    deposit_percentage: template.deposit_percentage ?? '',
                    number_of_installments: template.number_of_installments ?? '',
                    due_days_before_departure: template.due_days_before_departure ?? '',
                    buffer_days: template.buffer_days ?? 0,
                    installments: template.installments || [],
                });
            // reset form when modal closes.
            } else {
                setFormData({
                    template_name: '',
                    status: 'draft',
                    payment_type: '',
                    deposit_percentage: '',
                    number_of_installments: '',
                    due_days_before_departure: '',
                    buffer_days: 0,
                    installments: [],
                });
            }
            setErrors({});
            setIsSaving(false);
        }
    }, [open, template]);

    // Whenever number of installments changes -> update row count in form installments

    useEffect(() => {
        const count = parseInt(formData.number_of_installments) || 0;

        setFormData(prev => {
            const existing = prev.installments || [];

            const rows = Array.from({ length: count }, (_, i) => ({
                installment_number: i + 1,
                installment_percentage: existing[i]?.installment_percentage ?? '',
                days_before_departure: existing[i]?.days_before_departure ?? '',
            }));

            return { ...prev, installments: rows };
        });
    }, [formData.number_of_installments]);

    // update form data whenever user types in something.

    const handleChange = (key, value) => {
        const numericKeys = [
            'deposit_percentage',
            'due_days_before_departure',
            'number_of_installments',
            'buffer_days',
        ];
        const nextValue = numericKeys.includes(key) ? sanitizeNumberInput(value) : value;
        setFormData(prev => ({ ...prev, [key]: nextValue }));
    };

    const buildSavePayload = () => {
        const payload = {
            template_name: formData.template_name?.trim(),
            payment_type: formData.payment_type,
            status: formData.status,
        };
        const bufferDays = formData.buffer_days === '' || formData.buffer_days == null ? 0 : Number(formData.buffer_days);

        if (formData.payment_type === 'deposit_final_balance') {
            payload.deposit_percentage = formData.deposit_percentage === '' ? null : formData.deposit_percentage;
            payload.due_days_before_departure = formData.due_days_before_departure === '' ? null : formData.due_days_before_departure;
            payload.buffer_days = bufferDays;
        } else if (formData.payment_type === 'deposit_installments') {
            payload.deposit_percentage = formData.deposit_percentage === '' ? null : formData.deposit_percentage;
            payload.number_of_installments = formData.number_of_installments === '' ? null : formData.number_of_installments;
            payload.installments = formData.installments;
            payload.buffer_days = bufferDays;
        }

        return payload;
    };

    const handleSave = async () => {
        if (!validate()) return;
        setIsSaving(true);
        try {
            await onSave(buildSavePayload());
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') onClose();
            }}
            maxWidth="sm"
            fullWidth
        >

            {/* Dialog Header */}

            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {template ? 'Edit Payment Template' : 'Create Payment Template'}
                <IconButton onClick={onClose}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            {/* Dialog Form */}

            <DialogContent>

                <Grid container spacing={2} sx={{ mb: 2 }}>

                    {/* Form - Template Name */}

                    <Grid item xs={6}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box>
                                <Typography variant="caption">Template Name *</Typography>
                                <TextField
                                    size="small"
                                    fullWidth
                                    placeholder="e.g. Standard Split"
                                    value={formData.template_name}
                                    onChange={(e) => handleChange('template_name', e.target.value)}
                                    error={Boolean(errors.template_name)}
                                    helperText={errors.template_name || ' '}
                                    FormHelperTextProps={{ sx: { minHeight: 20, m: 0, mt: 0.5 } }}
                                />
                            </Box>
                        </Box>

                    </Grid>

                    {/* Form - Payment Type Dropdown */}

                    <Grid item xs={6}>
                        <Box>
                            <Typography variant="caption">Payment Type *</Typography>
                            <Select
                                size="small"
                                fullWidth
                                value={formData.payment_type}
                                error={Boolean(errors.payment_type)}
                                onChange={(e) => handleChange('payment_type', e.target.value)}
                            >
                                <MenuItem value="full_payment">Full Payment</MenuItem>
                                <MenuItem value="deposit_final_balance">Deposit + Final Balance</MenuItem>
                                <MenuItem value="deposit_installments">Deposit + Installments</MenuItem>
                            </Select>
                            <Typography variant="caption" color="error" sx={{ ...fieldErrorSx, minHeight: 20, visibility: errors.payment_type ? 'visible' : 'hidden' }}>
                                {errors.payment_type || ' '}
                            </Typography>
                        </Box>
                    </Grid>

                    {/* Form Field : Status dropdown*/}

                    <Grid item xs={6}>
                        <Box>
                            <Typography variant="caption">Status *</Typography>
                            <Select
                                size="small"
                                fullWidth
                                value={formData.status}
                                error={Boolean(errors.status)}
                                onChange={(e) => handleChange('status', e.target.value)}
                            >
                                <MenuItem value="draft">Draft</MenuItem>
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="inactive">Inactive</MenuItem>
                            </Select>
                            <Typography variant="caption" color="error" sx={{ ...fieldErrorSx, minHeight: 20, visibility: errors.status ? 'visible' : 'hidden' }}>
                                {errors.status || ' '}
                            </Typography>
                        </Box>
                    </Grid>

                    { (formData.payment_type === 'deposit_final_balance' || formData.payment_type === 'deposit_installments') &&
                        <Grid item xs={6}>
                            <Box>
                                <Typography variant="caption">Buffer Days</Typography>
                                <TextField
                                    size="small"
                                    fullWidth
                                    type="number"
                                    onKeyDown={blockInvalidNumberKeys}
                                    inputProps={{ min: 0 }}
                                    value={formData.buffer_days}
                                    onChange={(e) => handleChange('buffer_days', e.target.value)}
                                    error={Boolean(errors.buffer_days)}
                                    helperText={errors.buffer_days || 'Days after the due date before an unpaid order is auto-cancelled'}
                                    FormHelperTextProps={{ sx: { minHeight: 20, m: 0, mt: 0.5 } }}
                                />
                            </Box>
                        </Grid>
                    }

                </Grid>

                {/* IF payment_type selected is deposit_final_balance */}

                {formData.payment_type === 'deposit_final_balance' && (
                    <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, p: 2 }}>
                        <Grid container spacing={2}>

                            {/* Form Field : Deposit Percentage */}

                            <Grid item xs={6}>
                                <Box>
                                    <Typography variant="caption">Deposit % *</Typography>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        type="number"
                                        onKeyDown={blockInvalidNumberKeys}
                                        inputProps={{ min: 0, max: 100 }}
                                        value={formData.deposit_percentage}
                                        onChange={(e) => handleChange('deposit_percentage', e.target.value)}
                                        error={Boolean(errors.deposit_percentage)}
                                        helperText={errors.deposit_percentage || ' '}
                                        FormHelperTextProps={{ sx: { minHeight: 20, m: 0, mt: 0.5 } }}
                                    />
                                </Box>
                            </Grid>

                            {/* Form Field : Auto Evaluated Remaining Balance */}

                            <Grid item xs={6}>
                                <Box sx={{ position: 'relative' }}>
                                    <Typography variant="caption">Remaining Balance %</Typography>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        variant="outlined"
                                        disabled
                                        value={formData.deposit_percentage ? 100 - formData.deposit_percentage : ''}
                                        sx={disabledOutlinedSx}
                                    />
                                </Box>
                            </Grid>

                            {/* Form Field : Final Due ( Days Before Departure ) */}

                            <Grid item xs={6}>
                                <Box>
                                    <Typography variant="caption">Final Due (Days Before Departure) *</Typography>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        type="number"
                                        onKeyDown={blockInvalidNumberKeys}
                                        error={Boolean(errors.due_days_before_departure)}
                                        value={formData.due_days_before_departure}
                                        onChange={(e) => handleChange('due_days_before_departure', e.target.value)}
                                        helperText={errors.due_days_before_departure || ' '}
                                        FormHelperTextProps={{ sx: { minHeight: 20, m: 0, mt: 0.5 } }}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                )}

                {/* IF payment_type selected is deposit_installments */}

                {formData.payment_type === 'deposit_installments' && (
                    <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, p: 2 }}>
                        <Grid container spacing={2}>

                            {/* Form Field : Deposit Percentage */}

                            <Grid item xs={6}>
                                <Box>
                                    <Typography variant="caption">Deposit % *</Typography>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        type="number"
                                        onKeyDown={blockInvalidNumberKeys}
                                        inputProps={{ min: 0, max: 100 }}
                                        value={formData.deposit_percentage}
                                        onChange={(e) => handleChange('deposit_percentage', e.target.value)}
                                        error={Boolean(errors.deposit_percentage)}
                                        helperText={errors.deposit_percentage || ' '}
                                        FormHelperTextProps={{ sx: { minHeight: 20, m: 0, mt: 0.5 } }}
                                    />
                                </Box>
                            </Grid>

                            {/* Form Field : Number of Installments */}

                            <Grid item xs={6}>
                                <Box>
                                    <Typography variant="caption">Max Installments *</Typography>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        type="number"
                                        onKeyDown={blockInvalidNumberKeys}
                                        error={Boolean(errors.number_of_installments)}
                                        value={formData.number_of_installments}
                                        onChange={(e) => handleChange('number_of_installments', e.target.value)}
                                        helperText={
                                            errors.number_of_installments ||
                                            'Template slots; actual count is based on days until departure at booking'
                                        }
                                        FormHelperTextProps={{ sx: { minHeight: 20, m: 0, mt: 0.5 } }}
                                    />
                                </Box>
                            </Grid>
                        </Grid>

                        {/* dynamic installment rows will go here */}

                        {formData.installments.length > 0 && (
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="button" fontWeight="bold" sx={{ display: 'block', mb: 1 }}>
                                    Installment Schedule
                                </Typography>
                                {formData.installments.map((installment, index) => (
                                        <Grid container spacing={2} key={index} sx={{ mt: 0.5 }}>
                                            <Grid item xs={6}>
                                                <Box>
                                                    <Typography variant="caption">Installment {index + 1}: %</Typography>
                                                    <TextField
                                                        size="small"
                                                        fullWidth
                                                        type="number"
                                                        onKeyDown={blockInvalidNumberKeys}
                                                        error={Boolean(errors.installments?.[index]?.installment_percentage)}
                                                        value={installment?.installment_percentage !== undefined && installment?.installment_percentage !== null
                                                            ? parseFloat(installment.installment_percentage)
                                                            : ''
                                                        }
                                                        onChange={(e) => {
                                                            const updated = [...formData.installments];
                                                            updated[index].installment_percentage = sanitizeNumberInput(e.target.value);
                                                            handleChange('installments', updated);
                                                        }}
                                                        helperText={errors.installments?.[index]?.installment_percentage || ' '}
                                                        FormHelperTextProps={{ sx: { minHeight: 20, m: 0, mt: 0.5 } }}
                                                    />
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Box>
                                                    <Typography variant="caption">Days Before Departure</Typography>
                                                    <TextField
                                                        size="small"
                                                        fullWidth
                                                        type="number"
                                                        onKeyDown={blockInvalidNumberKeys}
                                                        error={Boolean(errors.installments?.[index]?.days_before_departure)}
                                                        value={installment.days_before_departure}
                                                        onChange={(e) => {
                                                            const updated = [...formData.installments];
                                                            updated[index].days_before_departure = sanitizeNumberInput(e.target.value);
                                                            handleChange('installments', updated);
                                                        }}
                                                        helperText={errors.installments?.[index]?.days_before_departure || ' '}
                                                        FormHelperTextProps={{ sx: { minHeight: 20, m: 0, mt: 0.5 } }}
                                                    />
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    ))}
                                {errors.percentageSum && (
                                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                                        Deposit % and all installment percentages must add up to exactly 100%.
                                    </Typography>
                                )}
                            </Box>
                        )}

                    </Box>
                )}

            </DialogContent>

            {/* Dialog Action Buttons*/}

            <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e2e8f0', gap: 1 }}>
                <MDButton variant="outlined" color="secondary" onClick={onClose} disabled={isSaving}>
                    Cancel
                </MDButton>
                <MDButton variant="gradient" color="info" onClick={handleSave} disabled={isSaving}>
                    {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save Template'}
                </MDButton>
            </DialogActions>
        </Dialog>
    )
}

export default PaymentTemplateModal;